import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import {
  createPerformanceBonus,
  getNextEligibleMonth,
  getPerformanceBonusDeductions,
  getPerformanceBonusPercentage,
} from "../../services/performanceBonusServices";
import * as Yup from "yup";
import createCustomSchema from "../../fieldValidator/fieldValidator";
import toast from "react-hot-toast";
import { companyID } from "../../auth";
import Loader from "../Loader";

const validationSchema = Yup.object({
  from_month: createCustomSchema("date", {
    message: "* Select From Month!",
  }),
  to_month: createCustomSchema("date", {
    message: "* Select To Month!",
  }),
  release_percentage: createCustomSchema("decimalNumber", {
    message: "* Enter Release Percentage!",
  }),
  release_date: createCustomSchema("date", {
    message: "* Enter Release Date!",
  }),
  remarks: createCustomSchema("paragraph", {
    message: "* Enter Remarks!",
    min: 10,
  }),
});

const CreatePerformaceBonus = ({ show, handleClose, employee }) => {
  const [formData, setFormData] = useState({
    from_month: null,
    to_month: null,
    pb_deducted_amount: "0",
    release_percentage: "",
    released_amount: "0",
    release_date: null,
    remarks: "",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const loginEmployeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "release_percentage" && (value < 0 || value > 100)) {
      setErrors((prev) => ({
        ...prev,
        release_percentage: "* Release percentage must be between 0 and 100!",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const close = () => {
    setFormData({
      from_month: null,
      to_month: null,
      pb_deducted_amount: "0",
      release_percentage: "0",
      released_amount: "0",
      release_date: null,
      remarks: "",
    });
    setErrors({});
    handleClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      await createPerformanceBonus({
        ...formData,
        employee_id: employee,
        released_by: loginEmployeeId,
      });
      toast.success("Performance bonus released successfully.");
      close();
    } catch (err) {
      if (err.inner) {
        const formErrors = {};
        err.inner.forEach((error) => {
          formErrors[error.path] = error.message;
        });
        setErrors(formErrors);
      } else {
        setErrors((prev) => ({
          ...prev,
          performance_bonus: "Something went wrong, please try again later.",
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceBonusPercentage = async () => {
    setFetching(true);
    try {
      const response = await getPerformanceBonusPercentage(
        employee,
        company_id
      );
      setFormData((prev) => ({
        ...prev,
        release_percentage: response.performance_bonus_percentage ?? "0",
      }));
    } catch (error) {
      console.error("Error fetching performance bonus percentage:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchNextEligibleMonth = async () => {
    setFetching(true);
    try {
      const response = await getNextEligibleMonth(employee);
      if (response.next_eligible_month) {
        setFormData((prev) => ({
          ...prev,
          from_month: response.next_eligible_month ?? null,
        }));
      }
    } catch (error) {
      console.error("Error fetching next eligible month:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchPerformanceBonusDeductions = async () => {
    setFetching(true);
    try {
      const response = await getPerformanceBonusDeductions(
        employee,
        formData.from_month,
        formData.to_month
      );
      setFormData((prev) => ({
        ...prev,
        pb_deducted_amount: response.total_performance_bonus || "0",
      }));
    } catch (error) {
      console.error("Error fetching performance bonus deductions:", error);
    } finally {
      setFetching(false);
    }
  };

  const calculateReleasedAmount = () => {
    const { pb_deducted_amount, release_percentage } = formData;
    if (pb_deducted_amount && release_percentage) {
      const releasedAmount =
        (parseFloat(pb_deducted_amount) * parseFloat(release_percentage)) / 100;
      setFormData((prev) => ({
        ...prev,
        released_amount: releasedAmount.toFixed(2),
      }));
    }
  };

  useEffect(() => {
    if (show) {
      fetchPerformanceBonusPercentage();
      fetchNextEligibleMonth();
    }
  }, [show, employee]);

  useEffect(() => {
    if (formData.from_month && formData.to_month) {
      fetchPerformanceBonusDeductions();
    }
  }, [formData.from_month, formData.to_month, employee]);

  useEffect(() => {
    if (formData.release_percentage && formData.pb_deducted_amount) {
      calculateReleasedAmount();
    }
  }, [formData.release_percentage, formData.pb_deducted_amount]);

  return (
    <Modal show={show} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Release Performance Bonus</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>From Month</Form.Label>
                <Form.Control
                  type="month"
                  name="from_month"
                  value={formData.from_month}
                  onChange={handleChange}
                  readOnly
                />
                <small className="text-danger">{errors.from_month}</small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>To Month</Form.Label>
                <Form.Control
                  type="month"
                  name="to_month"
                  min={
                    formData.from_month ?? new Date().toISOString().slice(0, 7)
                  }
                  max={new Date().toISOString().slice(0, 7)}
                  value={formData.to_month}
                  onChange={handleChange}
                />
                <small className="text-danger">{errors.to_month}</small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>PB Deducted Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="pb_deducted_amount"
                  value={formData.pb_deducted_amount}
                  readOnly
                />
                <small className="text-danger">
                  {errors.pb_deducted_amount}
                </small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Release %</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  name="release_percentage"
                  value={formData.release_percentage}
                  onChange={handleChange}
                />
                <small className="text-danger">
                  {errors.release_percentage}
                </small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Released Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="released_amount"
                  readOnly
                  value={formData.released_amount}
                />
                <small className="text-danger">{errors.released_amount}</small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Release Date</Form.Label>
                <Form.Control
                  type="month"
                  name="release_date"
                  min={new Date().toISOString().slice(0, 7)}
                  value={formData.release_date}
                  onChange={handleChange}
                />
                <small className="text-danger">{errors.release_date}</small>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  name="remarks"
                  rows={2}
                  value={formData.remarks}
                  onChange={handleChange}
                />
                <small className="text-danger">{errors.remarks}</small>
              </Form.Group>
            </Col>
          </Row>
          {errors.performance_bonus && (
            <div className="mt-3">
              <small className="text-danger">
                * {errors.performance_bonus}
              </small>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {" "}
        <Button variant="secondary" onClick={close} className="me-2">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading ? true : false}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Modal.Footer>
      {fetching && <Loader show={fetching} />}
    </Modal>
  );
};

export default CreatePerformaceBonus;
